import chainsData from "../../data/bd_chains.json";
import { detectNamespace, getEvmChainIdAsInt, normalizeChainID, } from "../../shared/caip2";
import { DEFAULT_RPC_BASE_PATH } from "../../shared/constants";
import { ChainInvalidChainIdError, ChainInvalidNamespaceError } from "../../shared/error";
import { getLogger } from "../../shared/logger";
import { isDecimalString } from "../../shared/utils";
import { _getHealthyRpcUrls, _getRpcProvider, _getRpcUrls, _isBdRpcUrl, _isChainSupported, _selectFirstHealthyRpcUrl, } from "./chain-service-rpc";
import { _callSolanaRpc, _confirmSolanaTransaction, _getSolanaNetwork, _getSolanaRpcUrl, _getSolanaVoteAccounts, _isValidSolanaAddress, } from "./chain-service-solana";
/**
 * Expand.network chain ID mappings for non-EVM chains.
 * Maps normalized CAIP-2 chain IDs to numeric chain IDs used by expand.network API.
 *
 * Keys are full CAIP-2 identifiers (namespace:reference) after normalization.
 * For example, "solana:mainnet" normalizes to "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp".
 *
 * @see https://docs.expand.network/ids/chain-ids
 */
const EXPAND_CHAIN_ID_MAPPINGS = {
    // Solana - @see https://github.com/ChainAgnostic/namespaces/blob/main/solana/caip2.md
    "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp": "900", // Solana mainnet
    "solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z": "901", // Solana devnet
    // Bitcoin - @see https://github.com/ChainAgnostic/namespaces/blob/main/bip122/caip2.md
    "bip122:000000000019d6689c085ae165831e93": "1800", // Bitcoin mainnet
    "bip122:000000000933ea01ad0ee984209779baaec3ced90fa3f408719526f8d77f4943": "1801", // Bitcoin testnet
    // Stacks - @see https://github.com/ChainAgnostic/namespaces/blob/main/stacks/caip2.md
    "stacks:1": "1700", // Stacks mainnet
    "stacks:2147483648": "1701", // Stacks testnet
    // Stellar - @see https://github.com/ChainAgnostic/namespaces/blob/main/stellar/caip2.md
    "stellar:pubnet": "1500", // Stellar mainnet
    "stellar:testnet": "1501", // Stellar testnet
    // XRP Ledger - @see https://github.com/ChainAgnostic/namespaces/blob/main/xrpl/caip2.md
    "xrpl:0": "1600", // XRP Ledger mainnet
    "xrpl:1": "1601", // XRP Ledger testnet
    // Near - @see https://github.com/ChainAgnostic/namespaces/blob/main/near/caip2.md
    "near:mainnet": "1200", // Near mainnet
    "near:testnet": "1201", // Near testnet
    // Algorand - @see https://github.com/ChainAgnostic/namespaces/blob/main/algorand/caip2.md
    "algorand:wGHE2Pwdvd7S12BL5FaOP20EGYesN73ktiC1qzkkit8": "1300", // Algorand mainnet
    "algorand:SGO1GKSzyE7IEPItTxCByw9x8FmnrCDexi9/cOUJOiI": "1301", // Algorand testnet
    // Aptos - @see https://github.com/ChainAgnostic/namespaces/blob/main/aptos/caip2.md
    "aptos:1": "1400", // Aptos mainnet
    "aptos:2": "1401", // Aptos testnet
    // Sui - @see https://github.com/ChainAgnostic/namespaces/blob/main/sui/caip2.md
    "sui:mainnet": "101", // Sui mainnet
    "sui:devnet": "102", // Sui devnet
    "sui:testnet": "103", // Sui testnet
    // Tron (proposed namespace)
    "tron:mainnet": "1000", // Tron mainnet
    "tron:0x2b6653dc": "1000", // Tron mainnet (genesis hash)
    "tron:nile": "1001", // Tron NILE testnet
    // TON (proposed namespace)
    "ton:mainnet": "1100", // TON mainnet
    "ton:-1": "1100", // TON masterchain
    "ton:testnet": "1101", // TON testnet
    // Starknet - @see https://github.com/ChainAgnostic/namespaces/blob/main/starknet/caip2.md
    "starknet:SN_MAIN": "400", // Starknet mainnet
    "starknet:SN_SEPOLIA": "401", // Starknet Sepolia testnet
};
/**
 * Converts a normalized CAIP-2 chain ID to an expand.network numeric chain ID.
 *
 * This function expects a normalized CAIP-2 format (e.g., "eip155:1", "solana:5eykt...").
 * - For EVM chains (eip155): extracts the numeric reference directly
 * - For non-EVM chains: looks up the expand.network mapping
 *
 * @see https://docs.expand.network/ids/chain-ids
 *
 * @param normalizedChainId - A normalized CAIP-2 chain ID (e.g., "eip155:1", "solana:5eykt...")
 * @returns The expand.network numeric chain ID string, or undefined if not supported
 *
 * @example
 * ```typescript
 * // EVM chains - extracts numeric reference directly
 * getExpandNetworkChainId("eip155:1")    // "1"
 * getExpandNetworkChainId("eip155:137")  // "137"
 *
 * // Non-EVM chains - looks up in mapping
 * getExpandNetworkChainId("solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp")  // "900"
 * getExpandNetworkChainId("bip122:000000000019d6689c085ae165831e93")  // "1800"
 * getExpandNetworkChainId("stellar:pubnet")  // "1500"
 * getExpandNetworkChainId("near:mainnet")    // "1200"
 * ```
 */
export function getExpandNetworkChainId(normalizedChainId) {
    if (!normalizedChainId || !normalizedChainId.includes(":")) {
        return undefined;
    }
    const parts = normalizedChainId.split(":");
    if (parts.length !== 2) {
        return undefined;
    }
    const [namespace, reference] = parts;
    if (!namespace || !reference) {
        return undefined;
    }
    // For EVM chains (eip155), the reference is already the numeric chain ID
    if (namespace === "eip155") {
        if (isDecimalString(reference)) {
            return reference;
        }
        return undefined;
    }
    // For non-EVM chains, look up the full CAIP-2 string in the flat mapping
    return EXPAND_CHAIN_ID_MAPPINGS[normalizedChainId];
}
/**
 * Normalizes any chain ID format to expand.network numeric format.
 *
 * This is a convenience function that combines CAIP-2 normalization with
 * expand.network chain ID conversion. It accepts various input formats and
 * returns the numeric chain ID used by expand.network APIs.
 *
 * @param chainId - Chain ID in various formats: "1", "0x1", "eip155:1", "solana:mainnet", etc.
 * @returns The expand.network numeric chain ID string, or undefined if not supported
 *
 * @example
 * ```typescript
 * // Numeric string input
 * normalizeToExpandChainId("1")        // "1"
 * normalizeToExpandChainId("137")      // "137"
 *
 * // Hex input
 * normalizeToExpandChainId("0x1")      // "1"
 * normalizeToExpandChainId("0x89")     // "137"
 *
 * // CAIP-2 input
 * normalizeToExpandChainId("eip155:1")     // "1"
 * normalizeToExpandChainId("eip155:137")   // "137"
 * normalizeToExpandChainId("solana:mainnet")  // "900"
 *
 * // Invalid/unsupported
 * normalizeToExpandChainId("")         // undefined
 * normalizeToExpandChainId("invalid")  // undefined
 * ```
 */
export function normalizeToExpandChainId(chainId) {
    if (!chainId || chainId.trim() === "") {
        return undefined;
    }
    try {
        // Detect namespace from input to properly normalize
        const namespace = chainId.includes(":") ? chainId.split(":")[0] : "eip155";
        const normalizedChainId = normalizeChainID(chainId, namespace);
        // Convert to expand.network numeric format
        return getExpandNetworkChainId(normalizedChainId);
    }
    catch {
        return undefined;
    }
}
/**
 * Parses a chain ID from any supported format to expand.network numeric format.
 * Throws an error if the chain ID is invalid or unsupported.
 *
 * @remarks
 * This is the standard method for converting chain IDs for API requests.
 * It accepts CAIP-2 format, numeric strings, or hex format.
 *
 * @param chainId - Chain ID in any format: "1", "0x1", "eip155:1", "solana:mainnet"
 * @param fieldName - Name of the field for error messages
 * @returns Numeric chain ID string for expand.network API
 * @throws Error if the chain ID is invalid or unsupported
 *
 * @example
 * ```typescript
 * parseChainIdToExpand("eip155:1", "chainId")       // "1"
 * parseChainIdToExpand("137", "dstChainId")         // "137"
 * parseChainIdToExpand("0x89", "srcChainId")        // "137"
 * parseChainIdToExpand("solana:mainnet", "chainId") // "900"
 * ```
 */
export function parseChainIdToExpand(chainId, fieldName) {
    const expandChainId = normalizeToExpandChainId(chainId);
    if (!expandChainId) {
        throw new Error(`Invalid ${fieldName} '${chainId}': must be a valid chain ID (e.g., '1', '137', 'eip155:1', 'solana:mainnet')`);
    }
    return expandChainId;
}
/**
 * Parses an optional chain ID from any supported format to expand.network numeric format.
 *
 * @param chainId - Chain ID in any format, or undefined
 * @param fieldName - Name of the field for error messages
 * @returns Numeric chain ID string for expand.network API, or undefined if input is undefined
 * @throws Error if the chain ID is provided but invalid
 *
 * @example
 * ```typescript
 * parseOptionalChainIdToExpand("eip155:1", "srcChainId")  // "1"
 * parseOptionalChainIdToExpand(undefined, "srcChainId")  // undefined
 * ```
 */
export function parseOptionalChainIdToExpand(chainId, fieldName) {
    if (!chainId)
        return undefined;
    return parseChainIdToExpand(chainId, fieldName);
}
/**
 * Normalizes a chain ID to CAIP-2 format.
 *
 * @remarks
 * This is used when the CAIP-2 format is needed (e.g., for internal lookups or responses).
 *
 * @param chainId - Chain ID in any format
 * @param fieldName - Name of the field for error messages
 * @returns Chain ID in CAIP-2 format
 * @throws Error if the chain ID is invalid
 *
 * @example
 * ```typescript
 * parseChainIdToCaip2("1", "chainId")         // "eip155:1"
 * parseChainIdToCaip2("0x89", "chainId")      // "eip155:137"
 * parseChainIdToCaip2("eip155:1", "chainId")  // "eip155:1"
 * ```
 */
export function parseChainIdToCaip2(chainId, fieldName) {
    try {
        const namespace = chainId?.includes(":") ? chainId.split(":")[0] : "eip155";
        return normalizeChainID(chainId, namespace);
    }
    catch {
        throw new Error(`Invalid ${fieldName} '${chainId}': must be a valid chain ID (e.g., '1', '137', 'eip155:1')`);
    }
}
/**
 * Service for querying supported blockchain chains and their metadata.
 *
 * @remarks
 * The ChainService provides comprehensive access to blockchain network information,
 * including supported chains, network types, RPC endpoints, and metadata. It serves
 * as the central registry for all blockchain networks supported by the BD SDK.
 *
 * **Key Features:**
 * - **Chain Discovery**: Access to all supported blockchain networks
 * - **Network Filtering**: Filter by mainnet/testnet environments
 * - **RPC Management**: Automatic RPC endpoint configuration with API keys
 * - **Metadata Access**: Comprehensive chain information and specifications
 * - **Dynamic Configuration**: Real-time chain data loading and processing
 * - **
 *
 * @example
 * **Basic Chain Service Setup**
 * ```typescript
 * import { ChainService } from "@blockdaemon/bd-sdk";
 *
 * const chainService = new ChainService({
 *   bdApiKey: "your-blockdaemon-api-key"
 * });
 *
 * // Get all supported chains
 * const allChains = await chainService.getAllChains();
 * console.log(`Supported chains: ${allChains.length}`);
 * ```
 *
 * @example
 * **Chain Discovery and Filtering**
 * ```typescript
 * // Get all mainnet chains
 * const mainnets = chainService.getMainnetChains();
 * console.log("Mainnet chains:", mainnets.map(c => c.chainName));
 *
 * // Get all testnet chains
 * const testnets = chainService.getTestnetChains();
 * console.log("Testnet chains:", testnets.map(c => c.chainName));
 * ```
 *
 * @example
 * **Specific Chain Lookup**
 * ```typescript
 * // Get Ethereum mainnet details
 * const ethereum = chainService.getChain("1");
 * if (ethereum) {
 *   console.log(`Chain: ${ethereum.chainName}`);
 *   console.log(`RPC URLs: ${ethereum.rpc.length}`);
 *   console.log(`Native currency: ${ethereum.nativeCurrency.symbol}`);
 * }
 * ```
 *
 * @example
 * **Multi-chain Application Setup**
 * ```typescript
 * // Setup for a multi-chain DeFi application
 * const supportedChains = ["1", "polygon-mainnet", "arbitrum-mainnet"];
 * const chainConfigs = [];
 *
 * for (const chainId of supportedChains) {
 *   const chain = chainService.getChain(chainId);
 *   if (chain) {
 *     chainConfigs.push({
 *       chainId,
 *       name: chain.chainName,
 *       rpcUrl: chain.rpc[0], // Use first RPC URL
 *       nativeCurrency: chain.nativeCurrency,
 *       blockExplorerUrl: chain.blockExplorerUrls?.[0]
 *     });
 *   }
 * }
 *
 * console.log("Multi-chain configuration:", chainConfigs);
 * ```
 */
export class ChainService {
    bdApiKey;
    logger;
    chains;
    options;
    rpcBasePath;
    supportedChains;
    providerMap = new Map();
    retryOptions;
    /**
     * Returns the numeric EVM chain ID for a given chain identifier.
     * Accepts CAIP-2 (e.g., "eip155:1") or similar inputs and normalizes them.
     *
     * @param chainId - Chain identifier in string/CAIP-2 format
     * @returns Numeric chain ID
     */
    static getNumericalChainID(chainId) {
        const caip2 = normalizeChainID(chainId, "eip155");
        return getEvmChainIdAsInt(caip2);
    }
    /**
     * Creates a new ChainService instance.
     *
     * @param options - Configuration options for the chain service
     * @throws {Error} If the BD API key is missing or empty
     *
     * @example
     * ```typescript
     * const chainService = new ChainService({
     *   bdApiKey: process.env.BD_API_KEY,
     *   logger: customLogger // optional
     * });
     * ```
     */
    constructor(options) {
        // Initialize logger first (fallback to default logger if none provided)
        this.logger = options.logger || getLogger({ level: "info", label: "bd-sdk-chain-service" });
        this.retryOptions = options.retryOptions;
        if (!options.bdApiKey || options.bdApiKey.trim() === "") {
            // Allow missing key; external environment may allow other forms of authentication
            this.bdApiKey = undefined;
            this.logger.warn("BD API key not provided. Some features may be unavailable.");
        }
        else {
            this.bdApiKey = options.bdApiKey;
        }
        this.options = options;
        this.rpcBasePath = options.rpcBasePath || DEFAULT_RPC_BASE_PATH;
        // Initialize chains map
        this.chains = new Map();
        this.supportedChains = [];
        this.initializeChains();
    }
    /**
     * Initialize the supported chains from static JSON data.
     * @internal
     */
    initializeChains() {
        this.logger.debug("Initializing chains from JSON data");
        const chains = chainsData;
        // Initialize supported chains mapping from bd_chains.json
        this.supportedChains = chains.map((chain) => ({
            chainName: chain.chainName,
            chainId: chain.chainId,
            networkType: chain.networkType,
            description: `${chain.chainName} ${chain.networkType}`,
        }));
        for (const chain of chains) {
            const usesBlockdaemonRpc = chain.rpc.some((url) => _isBdRpcUrl(url, this.rpcBasePath));
            if (usesBlockdaemonRpc && !this.bdApiKey) {
                this.logger.warn(`BD API key not provided. RPC requests to Blockdaemon endpoints for ${chain.chainName} may return 401 Unauthorized.`);
            }
            // Store RPCs as-is (placeholders removed from source data); headers will be injected at request time
            if (chain.rpc.length > 0) {
                this.chains.set(chain.chainId, chain);
            }
        }
        this.logger.debug(`Initialized ${this.chains.size} chains`);
    }
    /**
     * Retrieves all supported blockchain chains.
     *
     * @remarks
     * This method returns a comprehensive list of all blockchain networks
     * supported by the BD SDK, including both mainnet and testnet environments.
     * Each chain includes complete metadata, RPC endpoints, and network configuration.
     *
     * @returns Promise resolving to an array of all supported chains
     * @throws {Error} If the SDK configuration is not initialized
     *
     * @example
     * ```typescript
     * const allChains = await chainService.getAllChains();
     * console.log(`Total supported chains: ${allChains.length}`);
     *
     * // Display chain information
     * allChains.forEach(chain => {
     *   console.log(`${chain.chainName} (${chain.chainID})`);
     *   console.log(`  Network: ${chain.networkType}`);
     *   console.log(`  RPC URLs: ${chain.rpc.length}`);
     *   console.log(`  Native token: ${chain.nativeCurrency.symbol}`);
     * });
     * ```
     *
     * @example
     * ```typescript
     * // Filter and categorize chains
     * const allChains = await chainService.getAllChains();
     * const mainnetChains = allChains.filter(chain => chain.networkType === 'mainnet');
     * const testnetChains = allChains.filter(chain => chain.networkType === 'testnet');
     *
     * console.log(`Mainnet chains: ${mainnetChains.length}`);
     * console.log(`Testnet chains: ${testnetChains.length}`);
     * ```
     *
     */
    async getAllChains() {
        if (!this.options)
            throw new Error("SDK config not initialized");
        this.logger.debug("Getting all chains");
        return Array.from(this.chains.values());
    }
    /**
     * Retrieves a specific blockchain chain by its chain ID.
     *
     * @remarks
     * This method provides detailed information about a specific blockchain network,
     * including RPC endpoints, native currency details, block explorer URLs, and
     * network specifications. The chain ID should match the format used in the
     * BD SDK chain registry.
     *
     * @param chainID - The unique identifier for the blockchain chain
     * @returns Promise resolving to the chain details or undefined if not found
     * @throws {Error} If the SDK configuration is not initialized
     *
     * @example
     * ```typescript
     * // Get Ethereum mainnet details
     * const ethereum = await chainService.getChain("1");
     * if (ethereum) {
     *   console.log(`Chain: ${ethereum.chainName}`);
     *   console.log(`Chain ID: ${ethereum.chainID}`);
     *   console.log(`Network ID: ${ethereum.networkId}`);
     *   console.log(`RPC URLs: ${ethereum.rpc.join(", ")}`);
     *   console.log(`Native currency: ${ethereum.nativeCurrency.name} (${ethereum.nativeCurrency.symbol})`);
     *   console.log(`Block explorer: ${ethereum.blockExplorerUrls?.[0]}`);
     * } else {
     *   console.log("Chain not found");
     * }
     * ```
     *
     * @example
     * ```typescript
     * // Setup Web3 provider for a specific chain
     * const arbitrum = await chainService.getChain("arbitrum-mainnet");
     * if (arbitrum && arbitrum.rpc.length > 0) {
     *   const web3Provider = new Web3(arbitrum.rpc[0]);
     *   console.log("Web3 provider configured for Arbitrum");
     * }
     * ```
     *
     * @example
     * ```typescript
     * // Get chain configuration for wallet connection
     * const polygon = await chainService.getChain("polygon-mainnet");
     * if (polygon) {
     *   const walletConfig = {
     *     chainId: `0x${parseInt(polygon.networkId).toString(16)}`,
     *     chainName: polygon.chainName,
     *     rpcUrls: polygon.rpc,
     *     nativeCurrency: polygon.nativeCurrency,
     *     blockExplorerUrls: polygon.blockExplorerUrls
     *   };
     *   console.log("Wallet configuration:", walletConfig);
     * }
     * ```
     *
     */
    /**
     * Retrieves a specific blockchain chain by its chain ID.
     *
     * @remarks
     * This method provides detailed information about a specific blockchain network,
     * including RPC endpoints, native currency details, block explorer URLs, and
     * network specifications. The chain ID should match the format used in the
     * BD SDK chain registry.
     *
     * @param chainID - The unique identifier for the blockchain chain
     * @returns The chain details or undefined if not found
     * @throws {Error} If the SDK configuration is not initialized
     *
     * @example
     * ```typescript
     * // Get Ethereum mainnet details
     * const ethereum = chainService.getChain("1");
     * if (ethereum) {
     *   console.log(`Chain: ${ethereum.chainName}`);
     *   console.log(`Chain ID: ${ethereum.chainID}`);
     *   console.log(`RPC URLs: ${ethereum.rpc.join(", ")}`);
     *   console.log(`Native currency: ${ethereum.nativeCurrency.symbol}`);
     * }
     * ```
     *
     * @example
     * ```typescript
     * // Get chain configuration for wallet connection
     * const polygon = chainService.getChain("polygon-mainnet");
     * if (polygon) {
     *   const walletConfig = {
     *     chainId: `0x${parseInt(polygon.networkId).toString(16)}`,
     *     chainName: polygon.chainName,
     *     rpcUrls: polygon.rpc,
     *     nativeCurrency: polygon.nativeCurrency,
     *     blockExplorerUrls: polygon.blockExplorerUrls
     *   };
     *   console.log("Wallet configuration:", walletConfig);
     * }
     * ```
     *
     * @public
     */
    getChain(chainID) {
        if (!this.options)
            throw new Error("SDK config not initialized");
        this.logger.debug(`Getting chain with ID: ${chainID}`);
        const chainIDStr = String(chainID).trim();
        try {
            // Step 1: Detect namespace (eip155 or solana)
            const namespace = detectNamespace(chainIDStr);
            // Step 2: Normalize to CAIP-2 format
            const normalizedChainId = normalizeChainID(chainIDStr, namespace);
            // Step 3: Validate reference part based on namespace
            const parts = normalizedChainId.split(":");
            if (parts.length === 2) {
                this.validateReference(parts[0], parts[1]);
            }
            // Step 4: Lookup in chain registry
            return this.chains.get(normalizedChainId);
        }
        catch (error) {
            // If detection/normalization/validation fails, try direct lookup (for backwards compatibility)
            this.logger.debug(`Chain ID processing failed for ${chainID}, trying direct lookup: ${error}`);
            return this.chains.get(chainIDStr);
        }
    }
    /**
     * Validates the reference (suffix) based on the namespace.
     *
     * @remarks
     * Ensures the reference part of a chain ID is valid for the given namespace.
     * Works together with `detectNamespace` from caip2.ts - use `detectNamespace`
     * to determine the namespace first, then pass both to this method for validation.
     *
     * **Validation Rules:**
     *
     * **EIP155 (Ethereum/EVM):**
     * - Must be numeric string (decimal or hex)
     * - Examples: `"1"`, `"137"`, `"0x1"`
     *
     * **Solana:**
     * - Cluster alias: `"mainnet"`, `"testnet"` (verified in registry)
     * - Genesis hash: 32-character base58 string
     * - Truncated hash: Must exist in configured chains
     *
     * @param namespace - The blockchain namespace ("eip155" or "solana")
     * @param reference - The reference part to validate
     * @returns true if valid
     * @throws {Error} If reference is invalid for the namespace
     *
     * @example
     * ```typescript
     * validateReference("eip155", "1")        // ✓ valid
     * validateReference("eip155", "0x89")     // ✓ valid
     * validateReference("eip155", "abc")      // ✗ throws Error
     * validateReference("solana", "mainnet")  // ✓ valid (if in registry)
     * validateReference("solana", "5eykt...")  // ✓ valid (32-char base58)
     * ```
     *
     * @private
     */
    validateReference(namespace, reference) {
        if (namespace === "eip155") {
            // EVM: must be numeric (decimal or hex)
            const isDecimal = /^[0-9]+$/.test(reference);
            const isHex = /^0x[0-9a-f]+$/i.test(reference);
            if (!isDecimal && !isHex) {
                throw new ChainInvalidChainIdError(`${namespace}:${reference}`, namespace, "Must be numeric (decimal or hex format)");
            }
            return true;
        }
        if (namespace === "solana") {
            // Solana: cluster alias or genesis hash
            // Check if it's a known cluster alias
            if (reference === "mainnet" || reference === "testnet") {
                // Verify alias exists in chain registry
                const solanaChain = Array.from(this.chains.values()).find((c) => c.chainId === `solana:${reference}`);
                if (!solanaChain) {
                    throw new ChainInvalidChainIdError(`solana:${reference}`, namespace, "Not found in chain registry");
                }
                return true;
            }
            // Check if it's a 32-char base58 genesis hash
            if (/^[1-9A-HJ-NP-Za-km-z]{32}$/.test(reference)) {
                return true;
            }
            // Check if it's a valid reference in our chain registry (truncated hash)
            const solanaChain = Array.from(this.chains.values()).find((c) => c.chainId === `solana:${reference}`);
            if (solanaChain) {
                return true;
            }
            throw new ChainInvalidChainIdError(`solana:${reference}`, namespace, "Expected: cluster alias (mainnet/testnet), 32-char base58 genesis hash, or configured chain reference");
        }
        throw new ChainInvalidNamespaceError(`${namespace}:${reference}`);
    }
    /**
     * Retrieves a chain mapping by its chain ID.
     *
     * @remarks
     * This method provides a lightweight chain mapping object containing
     * essential chain information without the full chain specification.
     * Useful for quick lookups and chain categorization.
     *
     * @param chainID - The unique identifier for the blockchain chain
     * @returns The chain mapping object or undefined if not found
     *
     * @example
     * ```typescript
     * const ethereumMapping = chainService.getChainByID("1");
     * if (ethereumMapping) {
     *   console.log(`Chain: ${ethereumMapping.chainName}`);
     *   console.log(`Type: ${ethereumMapping.networkType}`);
     *   console.log(`Description: ${ethereumMapping.description}`);
     * }
     * ```
     *
     * @example
     * ```typescript
     * // Quick chain type check
     * const isMainnet = chainService.getChainByID("1")?.networkType === "mainnet";
     * console.log(`Ethereum mainnet: ${isMainnet}`);
     * ```
     */
    getChainByID(chainID) {
        // Normalize the chain ID to CAIP-2 format for consistent lookup
        try {
            const chainIdStr = String(chainID).trim();
            const namespace = detectNamespace(chainIdStr);
            const normalizedChainId = normalizeChainID(chainIdStr, namespace);
            return this.supportedChains.find((chain) => chain.chainId === normalizedChainId);
        }
        catch (error) {
            // If normalization fails, try direct lookup (for backwards compatibility)
            this.logger.debug(`Chain ID normalization failed for ${chainID}, trying direct lookup: ${error}`);
            return this.supportedChains.find((chain) => chain.chainId === chainID);
        }
    }
    /**
     * Retrieves a chain mapping by its chain name.
     *
     * @remarks
     * This method performs a case-insensitive search for a chain by its name.
     * Useful when you know the chain name but not the specific chain ID format.
     *
     * @param chainName - The name of the blockchain chain (case-insensitive)
     * @returns The chain mapping object or undefined if not found
     *
     * @example
     * ```typescript
     * const ethereumMapping = chainService.getChainByName("Ethereum");
     * if (ethereumMapping) {
     *   console.log(`Chain ID: ${ethereumMapping.chainId}`);
     *   console.log(`Network type: ${ethereumMapping.networkType}`);
     * }
     * ```
     *
     * @example
     * ```typescript
     * // Case-insensitive search
     * const polygonMapping = chainService.getChainByName("polygon");
     * if (polygonMapping) {
     *   console.log(`Found: ${polygonMapping.chainName}`);
     * }
     * ```
     *
     */
    getChainByName(chainName) {
        return this.supportedChains.find((chain) => chain.chainName.toLowerCase() === chainName.toLowerCase());
    }
    /**
     * Retrieves all mainnet blockchain chains.
     *
     * @remarks
     * This method filters the supported chains to return only production
     * mainnet environments. Mainnet chains are live production networks
     * with real economic value and are suitable for production applications.
     *
     * @returns Array of mainnet chain mappings
     *
     * @example
     * ```typescript
     * const mainnetChains = chainService.getMainnetChains();
     * console.log(`Available mainnet chains: ${mainnetChains.length}`);
     *
     * mainnetChains.forEach(chain => {
     *   console.log(`${chain.chainName} - ${chain.chainID}`);
     * });
     * ```
     *
     * @example
     * ```typescript
     * // Get mainnet chains for production deployment
     * const productionChains = chainService.getMainnetChains();
     * const supportedNetworks = productionChains.map(chain => ({
     *   id: chain.chainID,
     *   name: chain.chainName,
     *   type: "mainnet"
     * }));
     * ```
     *
     */
    getMainnetChains() {
        return this.supportedChains.filter((chain) => chain.networkType === "mainnet");
    }
    /**
     * Retrieves all testnet blockchain chains.
     *
     * @remarks
     * This method filters the supported chains to return only test
     * network environments. Testnet chains are used for development,
     * testing, and staging without real economic value.
     *
     * @returns Array of testnet chain mappings
     *
     * @example
     * ```typescript
     * const testnetChains = chainService.getTestnetChains();
     * console.log(`Available testnet chains: ${testnetChains.length}`);
     *
     * testnetChains.forEach(chain => {
     *   console.log(`${chain.chainName} - ${chain.chainID}`);
     * });
     * ```
     *
     * @example
     * ```typescript
     * // Setup development environment with testnets
     * const developmentChains = chainService.getTestnetChains();
     * const testConfigs = developmentChains.map(chain => ({
     *   id: chain.chainID,
     *   name: chain.chainName,
     *   type: "testnet",
     *   description: chain.description
     * }));
     * ```
     *
     */
    getTestnetChains() {
        return this.supportedChains.filter((chain) => chain.networkType === "testnet");
    }
    /**
     * Retrieves chains filtered by network type.
     *
     * @remarks
     * This method provides a flexible way to filter chains by their network type,
     * allowing you to get either mainnet or testnet chains based on your needs.
     *
     * @param networkType - The network type to filter by ("mainnet" or "testnet")
     * @returns Array of chain mappings matching the specified network type
     *
     * @example
     * ```typescript
     * // Get mainnet chains
     * const mainnets = chainService.getChainsByNetworkType("mainnet");
     * console.log(`Mainnet chains: ${mainnets.length}`);
     *
     * // Get testnet chains
     * const testnets = chainService.getChainsByNetworkType("testnet");
     * console.log(`Testnet chains: ${testnets.length}`);
     * ```
     *
     * @example
     * ```typescript
     * // Dynamic network selection
     * const isDevelopment = process.env.NODE_ENV === "development";
     * const networkType = isDevelopment ? "testnet" : "mainnet";
     * const targetChains = chainService.getChainsByNetworkType(networkType);
     *
     * console.log(`Using ${networkType} chains:`, targetChains.map(c => c.chainName));
     * ```
     *
     */
    getChainsByNetworkType(networkType) {
        return this.supportedChains.filter((chain) => chain.networkType === networkType);
    }
    /**
     * Retrieves all supported chain mappings.
     *
     * @remarks
     * This method returns a complete list of all supported blockchain chains
     * in a lightweight mapping format. This is useful for getting an overview
     * of all available chains without the full chain specifications.
     *
     * @returns Array of all supported chain mappings
     *
     * @example
     * ```typescript
     * const allChains = chainService.getAllSupportedChains();
     * console.log(`Total supported chains: ${allChains.length}`);
     *
     * // Create a summary of supported chains
     * const chainSummary = allChains.map(chain => ({
     *   name: chain.chainName,
     *   id: chain.chainID,
     *   type: chain.networkType
     * }));
     *
     * console.log("Chain summary:", chainSummary);
     * ```
     *
     * @example
     * ```typescript
     * // Group chains by network type
     * const allChains = chainService.getAllSupportedChains();
     * const grouped = allChains.reduce((acc, chain) => {
     *   if (!acc[chain.networkType]) {
     *     acc[chain.networkType] = [];
     *   }
     *   acc[chain.networkType].push(chain);
     *   return acc;
     * }, {} as Record<string, EChainMapping[]>);
     *
     * console.log("Grouped chains:", grouped);
     * ```
     *
     */
    getAllSupportedChains() {
        return this.supportedChains;
    }
    /** Builds the context object passed to extracted RPC helper functions. */
    get rpcContext() {
        return {
            bdApiKey: this.bdApiKey,
            rpcBasePath: this.rpcBasePath,
            logger: this.logger,
            chains: this.chains,
            retryOptions: this.retryOptions,
            providerMap: this.providerMap,
            getNumericalChainID: ChainService.getNumericalChainID,
            getChain: (id) => this.getChain(id),
        };
    }
    /**
     * Returns RPC URLs for a chain.
     */
    getRpcUrls(chainId) {
        return _getRpcUrls(this.rpcContext, chainId);
    }
    /**
     * Returns healthy RPC URLs (in priority order) for a chain.
     * Performs a simple preflight (getBlockNumber) with timeout per URL.
     */
    async getHealthyRpcUrls(chainId, timeoutMs = 2500) {
        return _getHealthyRpcUrls(this.rpcContext, chainId, timeoutMs);
    }
    /**
     * Selects the first healthy RPC URL for a chain (BD → public priority).
     * Returns undefined if none are healthy.
     */
    async selectFirstHealthyRpcUrl(chainId, timeoutMs = 2500) {
        return _selectFirstHealthyRpcUrl(this.rpcContext, chainId, timeoutMs);
    }
    /**
     * Gets a configured {@link JsonRpcProvider} for a specific chain with caching and ordered failover.
     *
     * @remarks
     * This method provides an RPC client with:
     * - **Provider caching**: Reuses one provider instance per normalized CAIP-2 chain id
     * - **Failover (ordered)**: Uses {@link getRpcCandidates} order; on transport-style failures,
     *   the next HTTP(S) endpoint is tried (lazy per-endpoint child providers)
     * - **No upfront health probe**: Unlike {@link getHealthyRpcUrls} / {@link selectFirstHealthyRpcUrl},
     *   this does not run `getBlockNumber` against every URL before returning
     * - **Chain validation**: Ensures the chain exists in bundled chain data before creating a provider
     *
     * The provider is configured with the appropriate RPC endpoints for the chain,
     * including authentication with BD API keys where available.
     *
     * @param chainId - The chain ID in CAIP-2 format (e.g., "eip155:1" for Ethereum)
     * @returns Promise resolving to a configured JsonRpcProvider
     * @throws {Error} If the chain is not supported or has no available RPC endpoints
     *
     * @example
     * **Basic Provider Usage**
     * ```typescript
     * // Get provider for Ethereum mainnet
     * const provider = await chainService.getRpcProvider("eip155:1");
     *
     * // Use provider for blockchain operations
     * const blockNumber = await provider.getBlockNumber();
     * const balance = await provider.getBalance("0x742d35Cc6634C0532925A3B8D4C9dB96C4B4d8B6");
     *
     * console.log(`Block: ${blockNumber}, Balance: ${balance}`);
     * ```
     *
     * @example
     * **Multi-chain Provider Setup**
     * ```typescript
     * const supportedChains = ["eip155:1", "eip155:25", "eip155:11155111"];
     * const providers = new Map();
     *
     * for (const chainId of supportedChains) {
     *   try {
     *     const provider = await chainService.getRpcProvider(chainId);
     *     providers.set(chainId, provider);
     *     console.log(`✅ Provider ready for ${chainId}`);
     *   } catch (error) {
     *     console.log(`❌ Failed to setup provider for ${chainId}:`, error.message);
     *   }
     * }
     * ```
     *
     * @example
     * **Gas Estimation with Provider**
     * ```typescript
     * const provider = await chainService.getRpcProvider("eip155:1");
     *
     * // Estimate gas for a transaction
     * const gasEstimate = await provider.estimateGas({
     *   to: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
     *   from: "0x742d35Cc6634C0532925A3B8D4C9dB96C4B4d8B6",
     *   value: "0",
     *   data: "0x"
     * });
     *
     * console.log(`Estimated gas: ${gasEstimate}`);
     * ```
     *
     * Upstream:
     * - ethers.js {@link JsonRpcProvider} for JSON-RPC calls (getBlockNumber, estimateGas, getFeeData)
     * - Blockdaemon RPC endpoints when available (X-API-Key header attached)
     *
     * @public
     */
    async getRpcProvider(chainId) {
        return _getRpcProvider(this.rpcContext, chainId);
    }
    /**
     * Validates if a chain is supported and has working RPC endpoints.
     *
     * @remarks
     * This method provides a quick way to check chain support without creating
     * a full provider instance. Useful for validation and feature detection.
     *
     * @param chainId - The chain ID to validate
     * @returns true if chain is supported, false otherwise
     *
     * @example
     * ```typescript
     * // Check if Ethereum is supported
     * const isEthereumSupported = chainService.isChainSupported("eip155:1");
     * console.log(`Ethereum supported: ${isEthereumSupported}`);
     *
     * // Validate chain before using
     * if (chainService.isChainSupported(chainId)) {
     *   const provider = await chainService.getRpcProvider(chainId);
     *   // Use provider...
     * } else {
     *   console.log(`Chain ${chainId} is not supported`);
     * }
     * ```
     *
     * @public
     */
    isChainSupported(chainId) {
        return _isChainSupported(this.rpcContext, chainId);
    }
    // ========== SOLANA HELPERS ==========
    /**
     * Maps a Solana CAIP-2 chain ID to a network identifier for API calls.
     *
     * @param chainId - The Solana chain identifier in CAIP-2 format.
     * @returns The normalized CAIP-2 chain ID: `"solana:mainnet"` or `"solana:testnet"`.
     * @throws {Error} If the chain ID is not a recognized Solana network.
     */
    getSolanaNetwork(chainId) {
        return _getSolanaNetwork(chainId);
    }
    /**
     * Validates a Solana address format (Base58, 32–44 chars).
     */
    isValidSolanaAddress(address) {
        return _isValidSolanaAddress(address);
    }
    /**
     * Gets the RPC URL for a Solana chain.
     */
    getSolanaRpcUrl(chainId) {
        return _getSolanaRpcUrl(chainId, this.rpcBasePath);
    }
    /**
     * Makes a JSON-RPC call to a Solana RPC endpoint.
     * @beta
     */
    async callSolanaRpc(chainId, method, params) {
        return _callSolanaRpc(chainId, method, params, this.rpcBasePath, this.bdApiKey, this.logger);
    }
    /**
     * Calls the Solana getVoteAccounts RPC method for a specific validator.
     *
     * @see {@link https://solana.com/docs/rpc/http/getvoteaccounts} Solana RPC Documentation
     */
    async getSolanaVoteAccounts(chainId, votePubkey, options) {
        return _getSolanaVoteAccounts(chainId, votePubkey, options, this.rpcBasePath, this.bdApiKey, this.logger);
    }
    /**
     * Polls until a Solana transaction signature reaches the target commitment
     * or the timeout expires.
     *
     * @internal
     */
    async confirmSolanaTransaction(chainId, signature, targetCommitment = "finalized", timeout, pollInterval) {
        return _confirmSolanaTransaction(chainId, signature, this.rpcBasePath, this.bdApiKey, this.logger, targetCommitment, timeout, pollInterval);
    }
}
//# sourceMappingURL=data:application/json;base64,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